import React from 'react';
import styled from 'styled-components';
import { InlineLoading } from '../LoadingSpinner/LoadingSpinner';

// One size scale for every button. Sizes adapt to the screen width: the full size on
// desktop, a step down on tablets and a compact step on phones, so controls never
// grow on small screens. Touch devices keep a 44px tap area (Apple HIG 44pt,
// Material 48dp) through an invisible extension, see TOUCH_HIT_AREA.
const TABLET = '@media (max-width: 1024px)';
const PHONE = '@media (max-width: 480px)';
const NARROW_PHONE = '@media (max-width: 380px)';
const TOUCH = '@media (pointer: coarse)';

const BUTTON_SCALE = {
  small: { desktop: [32, 13, 12], tablet: [30, 13, 12], phone: [28, 12, 10], narrowX: 8 },
  medium: { desktop: [40, 14, 20], tablet: [38, 14, 18], phone: [36, 14, 16], narrowX: 14 },
  large: { desktop: [48, 16, 28], tablet: [46, 15, 24], phone: [44, 15, 20], narrowX: 18 },
};

const buttonSize = props => BUTTON_SCALE[props.$compact ? 'small' : props.$size] || BUTTON_SCALE.medium;

const step = ([height, font, padX]) => `
  min-height: ${height}px;
  font-size: ${font}px;
  padding: 0 ${padX}px;
`;

const sizeCss = scale => `
  ${step(scale.desktop)}
  ${TABLET} { ${step(scale.tablet)} }
  ${PHONE} { ${step(scale.phone)} }
  ${NARROW_PHONE} { padding: 0 ${scale.narrowX}px; }
`;

const ICON_SCALE = {
  small: [32, 32, 28],
  medium: [40, 38, 36],
  large: [48, 46, 44],
};

const iconCss = ({ $size }) => {
  const [desktop, tablet, phone] = ICON_SCALE[$size] || ICON_SCALE.medium;
  const box = size => `width: ${size}px; height: ${size}px;`;
  return `
    ${box(desktop)}
    ${TABLET} { ${box(tablet)} }
    ${PHONE} { ${box(phone)} }
  `;
};

// Expands the tappable area to 44px on touch screens without changing the visible size.
const TOUCH_HIT_AREA = `
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: max(100%, 44px);
    height: max(100%, 44px);
    transform: translate(-50%, -50%);
  }
`;

const StyledBaseButton = styled.button`
  box-sizing: border-box;
  ${props => sizeCss(buttonSize(props))}
  border-radius: ${props => props.$square ? '8px' : '50px'};
  border: ${props => 
    props.$variant === 'outline' ? `2px solid ${props.theme.colors.primaryPink}` : 
    props.$variant === 'secondary' ? `1px solid ${props.theme.colors.border}` : 
    'none'
  };
  background: ${props => 
    props.$variant === 'outline' ? 'transparent' : 
    props.$variant === 'secondary' ? props.theme.colors.bg2 : 
    props.$variant === 'danger' ? props.theme.colors.error :
    props.theme.colors.primary
  };
  color: ${props => 
    props.$variant === 'outline' ? props.theme.colors.primaryPink : 
    props.$variant === 'secondary' ? props.theme.colors.text :
    props.$variant === 'danger' ? 'white' :
    'white'
  };
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  line-height: 1.2;
  font-weight: 500;
  transition: background-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-family: inherit;
  white-space: nowrap;

  svg {
    flex-shrink: 0;
  }
  
  /* Mobile touch optimizations */
  -webkit-tap-highlight-color: transparent;
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  user-select: none;
  touch-action: manipulation;
  
  ${TOUCH} {
    ${props => (props.$compact || props.$size === 'small' || !props.$size || props.$size === 'medium') && TOUCH_HIT_AREA}
  }

  
  &:hover:not(:disabled) {
    opacity: 0.9;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    background: ${props => 
      props.$variant === 'outline' ? 'transparent' : 
      props.$variant === 'secondary' ? props.theme.colors.bg2 : 
      props.$variant === 'danger' ? props.theme.colors.error :
      props.theme.colors.primaryDark || props.theme.colors.primary
    };
  }
  
  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
  }

  /* No sticky hover lift on touch screens */
  @media (hover: none) {
    &:hover:not(:disabled) {
      transform: none;
      box-shadow: none;
    }
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }

  ${props => props.$fullWidth && `
    width: 100%;
  `}

  ${props => props.$loading && `
    /* Content is swapped for a spinner in the wrapper component; keep dimensions stable. */
    cursor: progress;
  `}
`;

const StyledButton = StyledBaseButton;

const StyledPrimaryButton = styled(StyledBaseButton)`
  background: ${props => props.theme.colors.primary};
  color: white;

  &:hover:not(:disabled) {
    background: ${props => props.theme.colors.primaryDark};
  }
`;

const StyledSecondaryButton = styled(StyledBaseButton)`
  background: ${props => props.theme.colors.bg2};
  color: ${props => props.theme.colors.text};
  border: 1px solid ${props => props.theme.colors.border};

  &:hover:not(:disabled) {
    background: ${props => props.theme.colors.bg3};
  }
`;

const StyledOutlineButton = styled(StyledBaseButton)`
  background: transparent;
  color: ${props => props.theme.colors.primaryPink};
  border: 2px solid ${props => props.theme.colors.primaryPink};

  &:hover:not(:disabled) {
    background: ${props => props.theme.colors.primaryPink};
    color: white;
  }
`;

const StyledDangerButton = styled(StyledBaseButton)`
  background: ${props => props.theme.colors.error};
  color: white;

  &:hover:not(:disabled) {
    background: ${props => props.theme.colors.errorDark || '#d32f2f'};
  }
`;

// Transparent button with a subtle fill on hover, for low-emphasis actions.
const StyledGhostButton = styled(StyledBaseButton)`
  background: transparent;
  color: ${props => props.theme.colors.text};
  border: none;

  &:hover:not(:disabled) {
    background: ${props => props.theme.colors.bg3};
    box-shadow: none;
    transform: none;
  }
`;

// Brand-coloured label without a fill.
const StyledTextButton = styled(StyledBaseButton)`
  background: transparent;
  color: ${props => props.theme.colors.primaryPink};
  border: none;

  &:hover:not(:disabled) {
    background: ${props => props.theme.colors.bg3};
    box-shadow: none;
    transform: none;
  }
`;

// Inline link-style button: no padding, underlined on hover.
const StyledLinkButton = styled(StyledBaseButton)`
  background: transparent;
  color: ${props => props.theme.colors.primaryPink};
  border: none;
  padding: 0;
  min-height: 0;
  display: inline;
  font-size: inherit;
  font-weight: 500;

  &:hover:not(:disabled) {
    background: transparent;
    text-decoration: underline;
    box-shadow: none;
    transform: none;
    opacity: 1;
  }
`;

// Round icon-only button. Always pass an aria-label.
const StyledIconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-sizing: border-box;
  ${props => iconCss(props)}
  padding: 0;
  border: ${props => props.$variant === 'outline' ? `1px solid ${props.theme.colors.border}` : 'none'};
  border-radius: ${props => props.$square ? '8px' : '50%'};
  background: ${props =>
    props.$variant === 'filled' ? props.theme.colors.bg2 :
    props.$variant === 'primary' ? props.theme.colors.primary :
    'transparent'
  };
  color: ${props =>
    props.$variant === 'primary' ? 'white' :
    props.$variant === 'danger' ? props.theme.colors.error :
    props.theme.colors.text
  };
  cursor: pointer;
  font-family: inherit;
  transition: background-color 0.2s ease, color 0.2s ease, transform 0.2s ease;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;

  ${TOUCH} {
    ${TOUCH_HIT_AREA}
  }

  &:hover:not(:disabled) {
    background: ${props =>
      props.$variant === 'primary' ? props.theme.colors.primaryDark || props.theme.colors.primary :
      props.theme.colors.bg3
    };
  }

  @media (hover: none) {
    &:hover:not(:disabled) {
      background: ${props =>
        props.$variant === 'filled' ? props.theme.colors.bg2 :
        props.$variant === 'primary' ? props.theme.colors.primary :
        'transparent'
      };
    }
  }

  &:active:not(:disabled) {
    transform: scale(0.95);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    flex-shrink: 0;
  }
`;

// Selectable pill for filters, tabs and sort options.
const CHIP_SCALE = {
  normal: { desktop: [32, 14, 16], tablet: [30, 13, 14], phone: [28, 13, 14] },
  small: { desktop: [28, 13, 12], tablet: [26, 12, 12], phone: [24, 12, 10] },
};

const chipCss = size => {
  const scale = CHIP_SCALE[size === 'small' ? 'small' : 'normal'];
  return `
    ${step(scale.desktop)}
    ${TABLET} { ${step(scale.tablet)} }
    ${PHONE} { ${step(scale.phone)} }
  `;
};

const StyledChip = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  box-sizing: border-box;
  ${props => chipCss(props.$size)}
  border: 1px solid ${props => props.$active ? props.theme.colors.primary : props.theme.colors.border};
  border-radius: 50px;
  background: ${props => props.$active ? props.theme.colors.primary : 'transparent'};
  color: ${props => props.$active ? 'white' : props.theme.colors.text};
  font-family: inherit;
  font-weight: 500;
  white-space: nowrap;
  cursor: pointer;
  transition: background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;

  ${TOUCH} {
    ${TOUCH_HIT_AREA}
  }

  &:hover:not(:disabled) {
    background: ${props => props.$active ? props.theme.colors.primaryDark || props.theme.colors.primary : props.theme.colors.bg3};
  }

  @media (hover: none) {
    &:hover:not(:disabled) {
      background: ${props => props.$active ? props.theme.colors.primary : 'transparent'};
    }
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    flex-shrink: 0;
  }
`;

// Unstyled base for bespoke interactive controls (tabs, calendar cells, menu items,
// player controls, tiles). Extend with styled(UnstyledButton); prefer the other
// exports whenever the control is a standard action, icon or filter button.
const StyledUnstyledButton = styled.button`
  margin: 0;
  padding: 0;
  border: none;
  background: none;
  color: inherit;
  font: inherit;
  text-align: center;
  cursor: pointer;
  ${props => props.$rounded && 'border-radius: 50px;'}
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;

  &:disabled {
    cursor: not-allowed;
  }
`;

// Row of tabs. variant "pill" (default) or "underline"; use with TabItem.
const StyledTabBar = styled.div.attrs({ role: 'tablist' })`
  display: flex;
  align-items: center;
  gap: ${props => props.$variant === 'underline' ? '0' : '8px'};
  overflow-x: auto;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const StyledTabItem = styled(StyledUnstyledButton).attrs(props => ({
  role: 'tab',
  'aria-selected': !!props.$active,
}))`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  flex: ${props => props.$fill ? '1' : '0 0 auto'};
  white-space: nowrap;
  font-weight: ${props => props.$variant === 'underline' && !props.$active ? 400 : 600};
  transition: background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease;

  ${props => props.$variant === 'underline' ? `
    padding: 1rem 12px 0;
    color: ${props.$active ? props.theme.colors.primary : props.theme.colors.textSecondary};

    &::after {
      content: '';
      position: absolute;
      left: 0;
      right: 0;
      bottom: 0;
      height: 2px;
      background: ${props.$active ? props.theme.colors.primary : 'transparent'};
    }

    position: relative;
    padding-bottom: 0.75rem;

    &:hover:not(:disabled) {
      color: ${props.theme.colors.primary};
    }
  ` : `
    box-sizing: border-box;
    min-height: 36px;
    padding: 0 16px;
    border: 1px solid ${props.$active ? props.theme.colors.primary : props.theme.colors.border};
    border-radius: 50px;
    background: ${props.$active ? props.theme.colors.primary : props.theme.colors.bg2};
    color: ${props.$active ? 'white' : props.theme.colors.text};
    font-size: 14px;

    &:hover:not(:disabled) {
      background: ${props.$active ? props.theme.colors.primaryDark || props.theme.colors.primary : props.theme.colors.bg3};
    }

    @media (hover: none) {
      &:hover:not(:disabled) {
        background: ${props.$active ? props.theme.colors.primary : props.theme.colors.bg2};
      }
    }
  `}

  ${props => props.$variant === 'underline' ? '' : `
    ${TABLET} { min-height: 34px; }
    ${PHONE} { min-height: 32px; }
    ${TOUCH} { ${TOUCH_HIT_AREA} }
  `}

  svg {
    flex-shrink: 0;
  }
`;

// Full-width row inside dropdowns and context menus. $danger tints it red.
const StyledMenuItem = styled(StyledUnstyledButton).attrs({ role: 'menuitem' })`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  box-sizing: border-box;
  min-height: 40px;
  padding: 0 14px;
  text-align: left;
  font-size: 14px;
  color: ${props => props.$danger ? props.theme.colors.error : props.theme.colors.text};
  transition: background-color 0.15s ease;

  &:hover:not(:disabled) {
    background: ${props => props.$danger ? `${props.theme.colors.error}14` : props.theme.colors.bg3};
  }

  &:disabled {
    opacity: 0.5;
  }
`;

// Keep the exported buttons as real styled-components so `styled(Button)` composes
// transient props ($variant, $rounded, $size, $compact, …) instead of stripping them.
// `.attrs()` swaps the children for the branded InlineLoading indicator when $loading.
const withLoading = (StyledComponent) => styled(StyledComponent).attrs((props) => ({
  children: props.$loading ? <InlineLoading size={18} /> : props.children,
  disabled: props.disabled || props.$loading,
  'aria-busy': props.$loading || undefined,
}))``;

export const Button = withLoading(StyledButton);
export const PrimaryButton = withLoading(StyledPrimaryButton);
export const SecondaryButton = withLoading(StyledSecondaryButton);
export const OutlineButton = withLoading(StyledOutlineButton);
export const DangerButton = withLoading(StyledDangerButton);
export const GhostButton = withLoading(StyledGhostButton);
export const TextButton = withLoading(StyledTextButton);
export const LinkButton = withLoading(StyledLinkButton);
export const IconButton = StyledIconButton;
export const UnstyledButton = StyledUnstyledButton;
export const TabBar = StyledTabBar;
export const TabItem = StyledTabItem;
export const MenuItem = StyledMenuItem;
export const Chip = StyledChip;
